import { useCallback } from 'react';
import type { Theme } from '@atlassian/jira-common-constants/src/theme-preference.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useResource, createResource } from '@atlassian/react-resource-router';
import {
	THEME_PREFERENCES_ANALYTICS_SUBJECT,
	THEME_PREFERENCES_ANALYTICS_ACTION,
} from '../../common/constants.tsx';
import { setThemePreference as setThemePreferenceOld } from '../../services/index.tsx';
import type { ThemePreferenceResource } from './types.tsx';
import { useThemePreference as useThemePreferenceNew } from './use-theme-preference.tsx';

const getDataLoader = () =>
	import(
		/* webpackChunkName: "async-router-resources-theme-preference", jiraSpaEntry: "async-router-resources-theme-preference" */ './get-data'
	);

export const themePreferenceResource = createResource<ThemePreferenceResource>({
	type: 'RESOURCE_THEME_PREFERENCE',
	getKey: () => 'state',
	getDataLoader,
	maxAge: 5 * 60 * 1000,
	isCritical: true,
});

// TODO (DST): Should be deleted along with shared-user-preferences cleanup
/**
 * Original implementation of useThemePreference (pre shared-user-preferences feature)
 */
const useThemePreferenceOld = () => {
	const { data, update: updateThemePreferenceResource } = useResource(themePreferenceResource);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireThemeAnalytics = useCallback(
		(current?: Theme, next?: Theme) => {
			const OStheme =
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window?.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
					? 'dark'
					: 'light';
			const analyticsEvent = createAnalyticsEvent({
				action: THEME_PREFERENCES_ANALYTICS_ACTION,
				actionSubject: THEME_PREFERENCES_ANALYTICS_SUBJECT,
			});
			fireUIAnalytics(analyticsEvent, {
				newPreference: next,
				oldPreference: current,
				OStheme,
			});
		},
		[createAnalyticsEvent],
	);

	const setTheme = useCallback(
		async (theme: Theme): Promise<void> => {
			updateThemePreferenceResource(() => ({ theme }));
			if (data?.theme !== theme) {
				fireThemeAnalytics(data?.theme, theme);
				await setThemePreferenceOld(theme);
			}
		},
		[data?.theme, updateThemePreferenceResource, fireThemeAnalytics],
	);

	return {
		theme: data?.theme,
		setTheme,
	};
};

/**
 * Hook to get and set theme preference.
 * Delegates to either the new implementation (with shared-user-preferences)
 * or the old implementation based on the feature flag.
 */
export const useThemePreference = () => {
	// See https://hello.atlassian.net/wiki/spaces/JFP/pages/5033916021/Jira+Playbook+How+can+I+feature+gate+that#From-a-hook for conditional hooks
	return fg('shared-user-preferences')
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useThemePreferenceNew(themePreferenceResource)
		: // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useThemePreferenceOld();
};
